import { put, select, call, all, takeLatest } from "redux-saga/effects";
import ImportDataApi from "../api/ImportData";
import StorageApi from "../api/Storage";
import { Auth, ImportData } from "../actions/";
import { downloadFile, setParseUTCtimeToLocale } from "../../utils/";
// Type
import type { Action } from "deox";
import type { tAPI } from "../api/API";
import {
  TENANT_SETTINGS_IMPORT_MODELS,
  getImportDataCollection,
  getImportDataGroupCollection,
} from "../../types/importData.types";
import type {
  ImportDataGroup,
  TypeImportData,
} from "../../types/importData.types";
//Sagas for Import Data: users or rotations
export function* doImportDataFetch(
  action: Action<
    string,
    {
      typeImport: ImportDataGroup;
      cbs?: (data: any) => void;
      cbe?: (error: any) => void;
    }
  >
) {
  window.logger.log("doImportDataFetch", action);
  const { typeImport, cbs = null, cbe = null } = action.payload || {};
  const importDataType = getImportDataGroupCollection(typeImport);
  yield put(ImportData.importDataFetchRequest({ importDataType }));
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    // call the api to get latest import data uploaded
    let models: TypeImportData[];
    if (typeImport === "Rotations") {
      models = ["Rotations"];
    } else if (typeImport === "TenantSettings") {
      models = [...TENANT_SETTINGS_IMPORT_MODELS];
    } else {
      models = [
        "SuperCompanyAdministrators",
        "CompanyAdministrators",
        "Faculties",
        "InstitutionAdministrators",
        "Preceptors",
        "Students",
      ];
    }
    const {
      data: { imports: importData = [] },
    } = yield call(ImportDataApi.GetImportData, api, models, { token });
    yield put(
      ImportData.importDataFetchSuccess({
        importData,
        importDataType,
      })
    );
    if (cbs) {
      cbs(importData);
    }
  } catch (error) {
    const { data: errors } = error;
    window.logger.error("ErrorSaga - doImportDataFetch", error, errors);
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(ImportData.importDataFetchFailure({ importDataType }));
    if (cbe) {
      cbe(error);
    }
  }
}
export function* doImportDataSend(
  action: Action<
    string,
    {
      body: any;
      callbackSuccess?: (data: any) => void;
      callbackError?: (error: any, details: any) => void;
    }
  >
) {
  window.logger.log("doImportDataSend", action);
  yield put(ImportData.importDataSendRequest());
  const { body, callbackSuccess = null, callbackError = null } = action.payload;
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    // Upload CSV to S3 first, then send import with fileKey
    const userId: string = yield select(({ auth: { user } }) => user._id);
    const { key } = yield call(
      StorageApi.UploadFile,
      api,
      body.file,
      {
        belongsTo: "Users" as const,
        ownerId: userId,
        location: "temporary" as const,
      },
      { token }
    );
    // call the api to send import data with fileKey
    const { data } = yield call(
      ImportDataApi.SendImportData,
      api,
      { model: body.model, fileKey: key },
      { token }
    );
    const { import: importData } = data;
    yield put(
      ImportData.importDataSendSuccess({
        importData,
        importDataType: getImportDataCollection(body.model),
      })
    );
    if (callbackSuccess) {
      callbackSuccess(importData);
    }
  } catch (error) {
    const { data: errors } = error;
    window.logger.error("ErrorSaga - doImportDataSend", error, errors);
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(ImportData.importDataSendFailure());
    const { message, details } = errors || {};
    if (callbackError) {
      const errorMessage =
        message || error.message || "The file could not be uploaded.";
      callbackError(
        `${errorMessage} No import was created, so no completion notification will arrive.`,
        details
      );
    }
  }
}

export function* doImportGenerateCSV(
  action: Action<
    string,
    {
      body: object;
      importName: string;
      callbackSuccess?: () => void;
      callbackError?: (error: any, details: any) => void;
    }
  >
) {
  window.logger.log("doImportGenerateCSV", action);
  yield put(ImportData.importGenerateCSVRequest());
  const {
    body,
    importName = "",
    callbackSuccess = null,
    callbackError = null,
  } = action.payload;
  try {
    const { api, token }: { api: tAPI; token: string } = yield select(
      ({ api }) => api
    );
    // call the api to generate CSV to import data: users or rotations
    const {
      data: { blob },
    } = yield call(ImportDataApi.GenerateCSVImportData, api, body, { token });
    downloadFile(
      `Import ${importName} - ${setParseUTCtimeToLocale().format(
        "MMM DD of YYYY [at] HH:mm"
      )}.csv`,
      blob
    );
    yield put(ImportData.importGenerateCSVSuccess());
    if (callbackSuccess) {
      try {
        callbackSuccess();
      } catch (callbackError) {
        window.logger.error(
          "ErrorSaga - doImportGenerateCSV callbackSuccess",
          callbackError
        );
      }
    }
  } catch (error) {
    const { data: errors } = error || {};
    window.logger.error("ErrorSaga - doImportGenerateCSV", error, errors);
    if (errors) {
      yield put(Auth.authCheckSessionExpiredApp({ errors }));
    }
    yield put(ImportData.importGenerateCSVFailure());
    const { message, details } = errors || {};
    if (callbackError) {
      try {
        callbackError(message, details);
      } catch (callbackFailure) {
        window.logger.error(
          "ErrorSaga - doImportGenerateCSV callbackError",
          callbackFailure
        );
      }
    }
  }
}

export default function* importDataSagas(): Generator<any, any, any> {
  yield all([
    takeLatest(ImportData.IMPORT_DATA_FETCH, doImportDataFetch),
    takeLatest(ImportData.IMPORT_DATA_SEND, doImportDataSend),
    takeLatest(ImportData.IMPORT_GENERATE_CSV, doImportGenerateCSV),
  ]);
}
